/**
 * Send Template Modal Component
 * Form per inviare template a destinatari
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';

const SendTemplateModal = ({ template, onClose, onSent }) => {
  const { t } = useTranslation();
  
  const [mode, setMode] = useState('immediate'); // 'immediate' or 'scheduled'
  const [formData, setFormData] = useState({
    recipient_phone: '',
    recipient_name: '',
    variables: {},
    scheduled_for: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize variables from template
  useEffect(() => {
    if (template?.variables) {
      const initialVars = {};
      template.variables.forEach(v => {
        initialVars[v.name] = '';
      });
      setFormData(prev => ({ ...prev, variables: initialVars }));
    }
  }, [template]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVariableChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      variables: { ...prev.variables, [name]: value }
    }));
  };

  const validatePhone = (phone) => {
    // Remove spaces and dashes
    const cleaned = phone.replace(/[\s-]/g, '');
    // Check if valid international format
    return /^\+?[1-9]\d{1,14}$/.test(cleaned);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate
    if (!formData.recipient_phone) {
      setError(t('templates.send.errors.phoneRequired'));
      return;
    }

    if (!validatePhone(formData.recipient_phone)) {
      setError(t('templates.send.errors.invalidPhone'));
      return;
    }

    // Check all required variables are filled
    if (template.variables) {
      const missingVars = template.variables.filter(v => !formData.variables[v.name]);
      if (missingVars.length > 0) {
        setError(t('templates.send.errors.missingVariables', { 
          vars: missingVars.map(v => v.name).join(', ') 
        }));
        return;
      }
    }

    try {
      setLoading(true);

      if (mode === 'immediate') {
        // Send immediately
        const response = await api.post('/api/v1/templates/send', {
          template_id: template.id,
          recipient_phone: formData.recipient_phone,
          variables: formData.variables
        });

        if (response.data.success) {
          setSuccess(t('templates.send.success'));
          setTimeout(() => onSent(), 1500);
        } else {
          setError(response.data.error || t('templates.send.errors.failed'));
        }
      } else {
        // Schedule for later
        if (!formData.scheduled_for) {
          setError(t('templates.send.errors.dateRequired'));
          return;
        }

        await api.post('/api/v1/templates/schedule', {
          template_id: template.id,
          recipient_phone: formData.recipient_phone,
          recipient_name: formData.recipient_name,
          variables: formData.variables,
          scheduled_for: formData.scheduled_for
        });

        setSuccess(t('templates.send.scheduled'));
        setTimeout(() => onSent(), 1500);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t('templates.send.errors.failed')));
    } finally {
      setLoading(false);
    }
  };

  // Get min datetime for scheduler (now + 5 minutes)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return now.toISOString().slice(0, 16);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📤 {t('templates.send.title')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Template Info */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#dcfce7', borderRadius: '8px' }}>
              <strong>{template.name}</strong>
              <span style={{ marginLeft: '1rem', fontSize: '0.85rem', color: '#16a34a' }}>
                ✅ {t('templates.status.approved')}
              </span>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="error-banner" style={{ marginBottom: '1rem' }}>
                <span>⚠️ {error}</span>
              </div>
            )}
            {success && (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#dcfce7', borderRadius: '8px', color: '#16a34a' }}>
                ✅ {success}
              </div>
            )}

            {/* Mode Toggle */}
            <div className="form-group">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setMode('immediate')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: mode === 'immediate' ? '#25D366' : '#f3f4f6',
                    color: mode === 'immediate' ? 'white' : '#1a1a2e',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: mode === 'immediate' ? 600 : 400
                  }}
                >
                  ⚡ {t('templates.send.immediate')}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('scheduled')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: mode === 'scheduled' ? '#25D366' : '#f3f4f6',
                    color: mode === 'scheduled' ? 'white' : '#1a1a2e',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: mode === 'scheduled' ? 600 : 400
                  }}
                >
                  ⏰ {t('templates.send.schedule')}
                </button>
              </div>
            </div>

            {/* Recipient Phone */}
            <div className="form-group">
              <label>{t('templates.send.phone')} *</label>
              <input
                type="tel"
                name="recipient_phone"
                value={formData.recipient_phone}
                onChange={handleChange}
                placeholder="+39 333 123 4567"
              />
              <span className="hint">{t('templates.send.phoneHint')}</span>
            </div>

            {/* Recipient Name (optional, for scheduled) */}
            {mode === 'scheduled' && (
              <div className="form-group">
                <label>{t('templates.send.name')}</label>
                <input
                  type="text"
                  name="recipient_name"
                  value={formData.recipient_name}
                  onChange={handleChange}
                  placeholder="Mario Rossi"
                />
              </div>
            )}

            {/* Scheduled Date/Time */}
            {mode === 'scheduled' && (
              <div className="form-group">
                <label>{t('templates.send.scheduledFor')} *</label>
                <input
                  type="datetime-local"
                  name="scheduled_for"
                  value={formData.scheduled_for}
                  onChange={handleChange}
                  min={getMinDateTime()}
                />
              </div>
            )}

            {/* Variables */}
            {template.variables && template.variables.length > 0 && (
              <div className="form-group">
                <label>{t('templates.send.variables')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {template.variables.map((variable) => (
                    <div key={variable.position}>
                      <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#666' }}>
                        {`{{${variable.position}}} ${variable.name}`}
                      </label>
                      <input
                        type="text"
                        value={formData.variables[variable.name] || ''}
                        onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                        placeholder={variable.example || `Inserisci ${variable.name}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Fill Examples */}
            {template.name.includes('appuntamento') && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '0.85rem', marginBottom: '0.5rem', color: '#666' }}>
                  💡 {t('templates.send.quickFill')}
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setFormData(prev => ({
                      ...prev,
                      variables: {
                        ...prev.variables,
                        data: tomorrow.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' }),
                        orario: '15:00'
                      }
                    }));
                  }}
                  style={{ padding: '0.5rem 1rem', background: '#e5e7eb', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  📅 {t('templates.send.fillTomorrow')}
                </button>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose}
              className="btn-sync"
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-create"
              disabled={loading || success}
            >
              {loading ? '...' : (mode === 'immediate' ? t('templates.send.sendNow') : t('templates.send.scheduleBtn'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendTemplateModal;
